import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { ArrowRight, Eye, Target, Users, Brain, Shield, TrendingUp, CheckCircle, Layers, BarChart3 } from 'lucide-react';

const impactASPlusLogo = "/opt/brand/impactas-final-2.webp";
const leadershipMirrorLogo = "/opt/photo/leadershipmirror-2.webp";

const whatItReveals = [
  "How leaders see themselves versus how others experience them",
  "Patterns of behaviour that shape trust, influence, and execution",
  "Blind spots and development priorities",
  "The gap between intent and impact"
];

const applicationAreas = [
  "Leadership development programmes",
  "Executive coaching engagements",
  "Succession planning and readiness assessments",
  "Board and executive team effectiveness reviews",
  "High-potential identification and development"
];

const reportComponents = [
  {
    title: "Self-perception analysis",
    description: "How the leader views their own leadership approach, strengths, and areas for development"
  },
  {
    title: "Multi-rater feedback synthesis",
    description: "Aggregated perspectives from direct reports, peers, and senior stakeholders"
  },
  {
    title: "Gap analysis",
    description: "Where self-perception aligns or diverges from others' experience"
  },
  {
    title: "Development priorities",
    description: "Actionable focus areas informed by the data and organisational context"
  }
];

const LeadershipMirrorPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="360 Leadership Feedback Nigeria | Executive 360 Assessment Lagos"
        description="Leadership Mirror™ 360 degree feedback assessment for Nigerian executives. Comprehensive multi-rater feedback revealing leadership blind spots and strengths. Used by top Nigerian CEOs and senior leaders for targeted development."
        keywords="360 feedback Nigeria, leadership 360 assessment Lagos, executive 360 review Nigeria, multi-rater feedback Lagos, leadership feedback Nigeria, 360 degree appraisal Africa, executive feedback assessment Nigeria, leadership blind spots Lagos"
        canonical="/leadership-mirror"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section 
          className="relative min-h-[70vh] lg:min-h-[75vh] flex items-center bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(10, 10, 10, 0.95) 0%, rgba(10, 10, 10, 0.8) 40%, rgba(10, 10, 10, 0.4) 100%), url('/opt/photo/ext/u1573164574511-73c773193279.webp')`
          }}
        >
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8 pt-24">
            <div className="max-w-3xl">
              <h1 className="text-white font-semibold text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight mb-6">
                Leadership Mirror™
              </h1>
              
              <p className="text-[#2693d0] text-lg lg:text-xl xl:text-2xl font-medium leading-relaxed mb-8">
                Insight into leadership identity, behaviour, and impact
              </p>
              
              <p className="text-slate-300 text-base lg:text-lg leading-relaxed">
                A proprietary RESILIGENT diagnostic that surfaces the gap between how leaders see themselves and how they are experienced by others.
              </p>
            </div>
            
            {/* Logo badge - top right */}
            <div className="absolute top-24 right-6 lg:right-8 xl:right-16 hidden md:block">
              <img 
                src={leadershipMirrorLogo} 
                alt="Leadership Mirror" 
                className="h-16 lg:h-20 xl:h-24 w-auto opacity-90"
              />
            </div>
          </div>
        </section>

        {/* Introduction */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-4xl">
              <p className="text-slate-700 text-lg lg:text-xl leading-relaxed mb-6">
                Leadership Mirror™ is RESILIGENT's proprietary multi-rater diagnostic, designed to reveal how leaders perceive themselves, how others experience them, and where the leadership gaps lie.
              </p>
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                Unlike generic 360-degree tools, Leadership Mirror™ is calibrated for African leadership contexts, integrating behavioural feedback with insight into trust, influence, and execution dynamics.
              </p>
              <div className="bg-[#2693d0]/5 rounded-xl p-6 border border-[#2693d0]/20">
                <p className="text-slate-800 font-medium text-lg leading-relaxed">
                  The result is a clear, actionable view of leadership effectiveness that supports targeted development, coaching, and succession decisions.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What It Reveals */}
        <section className="bg-[#f0f5f8] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-[#2693d0]/10 flex items-center justify-center">
                    <Eye className="w-5 h-5 text-[#2693d0]" />
                  </div>
                  <span className="text-[#2693d0] text-sm font-semibold tracking-wide uppercase">Core Insight</span>
                </div>
                <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                  What Leadership Mirror™ Reveals
                </h2>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                  Effective leadership depends on more than intent. It depends on how others experience that leadership in practice.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-8 card-pulse-border">
                <p className="text-slate-700 font-medium text-lg mb-6">Leadership Mirror™ surfaces:</p>
                <ul className="space-y-4">
                  {whatItReveals.map((item, index) => (
                    <li key={index} className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-lg bg-[#2693d0]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle className="w-4 h-4 text-[#2693d0]" />
                      </div>
                      <span className="text-slate-700 text-base lg:text-lg leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Report Components */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl mb-12">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                Report Structure
              </h2>
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                Each Leadership Mirror™ report provides a structured view of leadership effectiveness from multiple perspectives.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {reportComponents.map((component, index) => (
                <div key={index} className="bg-[#f0f5f8] rounded-xl p-6 border border-slate-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-[#2693d0] flex items-center justify-center text-white font-semibold text-sm">
                      {index + 1}
                    </div>
                    <h3 className="text-slate-800 font-semibold text-lg">{component.title}</h3>
                  </div>
                  <p className="text-slate-600 text-base leading-relaxed">{component.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Applications */}
        <section className="bg-[#1e3a5f] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <h2 className="text-white font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                  Common Applications
                </h2>
                <p className="text-slate-200 text-base lg:text-lg leading-relaxed">
                  Leadership Mirror™ is deployed wherever organisations need clarity on leadership effectiveness, development needs, or readiness for expanded responsibility.
                </p>
              </div>
              
              <div>
                <ul className="space-y-4">
                  {applicationAreas.map((item, index) => (
                    <li key={index} className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-[#2693d0]" />
                      </div>
                      <span className="text-white text-base lg:text-lg leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Delivered via Impact AS Plus */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <img 
                  src={impactASPlusLogo} 
                  alt="Impact AS Plus" 
                  className="h-12 lg:h-14 w-auto mb-8"
                />
                <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                  Delivered Through Impact AS Plus™
                </h2>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                  Leadership Mirror™ is administered and reported through Impact AS Plus™, enabling secure data collection, aggregation, and integration with other diagnostic and development workflows.
                </p>
              </div>
              
              <div className="bg-[#f0f5f8] rounded-xl p-8 border border-slate-100">
                <p className="text-slate-700 font-medium text-lg mb-4">This integration ensures:</p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-700 text-base leading-relaxed">Confidentiality and data security</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-700 text-base leading-relaxed">Consistent interpretation across cohorts</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-700 text-base leading-relaxed">Connection to coaching and development plans</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-700 text-base leading-relaxed">Longitudinal tracking of leadership growth</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#f0f5f8] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                Learn More About Leadership Mirror™
              </h2>
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-10">
                Discover how Leadership Mirror™ can support leadership development, succession, and coaching in your organisation.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/sample-reports"
                  className="inline-flex items-center justify-center gap-2 bg-[#2693d0] text-white px-8 py-4 rounded-lg font-medium hover:bg-[#1e7ab8] transition-all duration-300"
                >
                  View sample reports
                  <ArrowRight className="w-5 h-5" />
                </a>
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-white text-slate-800 px-8 py-4 rounded-lg font-medium border border-slate-200 hover:border-[#2693d0] hover:text-[#2693d0] transition-all duration-300"
                >
                  Discuss your needs
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer hidePreFooterCta={true} />
    </div>
  );
};

export default LeadershipMirrorPage;
